<script>
// Constantes para os IDs (não mudam com o idioma)
const MODULE_IDS = [
  'auto_broadcasts',
  'scheduling',
  'products_services',
  'api_integration'
];

const QUANTITY_IDS = [
  'additional_ai_agents',
  'additional_channels',
  'additional_attendants',
  'additional_storage',
  'extra_ai_tokens'
];

const resolveBasePrice = pricing => {
  const candidate = Number(pricing?.base);
  return Number.isFinite(candidate) ? candidate : null;
};

const resolveModulePrices = pricing => {
  const prices = {};
  MODULE_IDS.forEach(key => {
    const candidate = Number(pricing?.modules?.[key]);
    prices[key] = Number.isFinite(candidate) ? candidate : null;
  });
  return prices;
};

const resolveQuantityPrices = pricing => {
  const prices = {};
  QUANTITY_IDS.forEach(key => {
    const candidate = Number(pricing?.quantities?.[key]);
    prices[key] = Number.isFinite(candidate) ? candidate : null;
  });
  return prices;
};

export default {
  name: 'ModularPlanSetup',
  props: {
    config: {
      type: Object,
      required: true,
    },
    pricing: {
      type: Object,
      default: () => ({
        base: null,
        modules: {},
        quantities: {},
      }),
    },
    showBackButton: {
      type: Boolean,
      default: true,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    summaryOnly: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    summaryTopClass: {
      type: String,
      default: 'lg:top-6',
    },
  },
  emits: ['update:config', 'continue', 'back'],
  data() {
    return {
      localConfig: this.normalizeInitialConfig(this.config),
    };
  },
  computed: {
    basePrice() {
      return resolveBasePrice(this.pricing);
    },
    modulePrices() {
      return resolveModulePrices(this.pricing);
    },
    quantityPrices() {
      return resolveQuantityPrices(this.pricing);
    },
    basePlan() {
      return {
        id: 'base',
        name: this.$t('PLANS.MODULAR.BASE.NAME'),
        description: this.$t('PLANS.MODULAR.BASE.DESCRIPTION'),
        price: this.basePrice,
      };
    },
    moduleOptions() {
      return [
        {
          id: 'auto_broadcasts',
          name: this.$t('PLANS.MODULAR.MODULES.AUTO_BROADCASTS.NAME'),
          description: this.$t('PLANS.MODULAR.MODULES.AUTO_BROADCASTS.DESCRIPTION'),
          price: this.modulePrices.auto_broadcasts,
        },
        {
          id: 'scheduling',
          name: this.$t('PLANS.MODULAR.MODULES.SCHEDULING.NAME'),
          description: this.$t('PLANS.MODULAR.MODULES.SCHEDULING.DESCRIPTION'),
          price: this.modulePrices.scheduling,
        },
        {
          id: 'products_services',
          name: this.$t('PLANS.MODULAR.MODULES.PRODUCTS_SERVICES.NAME'),
          description: this.$t('PLANS.MODULAR.MODULES.PRODUCTS_SERVICES.DESCRIPTION'),
          price: this.modulePrices.products_services,
        },
        {
          id: 'api_integration',
          name: this.$t('PLANS.MODULAR.MODULES.API_INTEGRATION.NAME'),
          description: this.$t('PLANS.MODULAR.MODULES.API_INTEGRATION.DESCRIPTION'),
          price: this.modulePrices.api_integration,
        },
      ];
    },
    modules() {
      return this.moduleOptions;
    },
    quantityOptions() {
      return [
        {
          id: 'additional_ai_agents',
          name: this.$t('PLANS.MODULAR.QUANTITIES.AI_AGENT.NAME'),
          description: this.$t('PLANS.MODULAR.QUANTITIES.AI_AGENT.DESCRIPTION'),
          price: this.quantityPrices.additional_ai_agents,
        },
        {
          id: 'additional_channels',
          name: this.$t('PLANS.MODULAR.QUANTITIES.CHANNEL.NAME'),
          description: this.$t('PLANS.MODULAR.QUANTITIES.CHANNEL.DESCRIPTION'),
          price: this.quantityPrices.additional_channels,
        },
        {
          id: 'additional_attendants',
          name: this.$t('PLANS.MODULAR.QUANTITIES.ATTENDANT.NAME'),
          description: this.$t('PLANS.MODULAR.QUANTITIES.ATTENDANT.DESCRIPTION'),
          price: this.quantityPrices.additional_attendants,
        },
        {
          id: 'additional_storage',
          name: this.$t('PLANS.MODULAR.QUANTITIES.STORAGE.NAME'),
          description: this.$t('PLANS.MODULAR.QUANTITIES.STORAGE.DESCRIPTION'),
          price: this.quantityPrices.additional_storage,
        },
        {
          id: 'extra_ai_tokens',
          name: this.$t('PLANS.MODULAR.QUANTITIES.AI_TOKENS.NAME'),
          description: this.$t('PLANS.MODULAR.QUANTITIES.AI_TOKENS.DESCRIPTION'),
          price: this.quantityPrices.extra_ai_tokens,
        },
      ];
    },
    quantities() {
      return this.quantityOptions;
    },
    totalPrice() {
      if (!Number.isFinite(this.basePrice)) return null;

      let total = this.basePrice;

      for (const module of this.moduleOptions) {
        if (!this.localConfig.modules[module.id]) continue;
        const price = this.modulePrices[module.id];
        if (!Number.isFinite(price)) return null;
        total += price;
      }

      for (const item of this.quantityOptions) {
        const qty = Number(this.localConfig.quantities[item.id] || 0);
        if (!qty) continue;
        const price = this.quantityPrices[item.id];
        if (!Number.isFinite(price)) return null;
        total += qty * price;
      }

      return total;
    },
    selectedLineItems() {
      const items = [
        {
          id: this.basePlan.id,
          name: this.basePlan.name,
          description: this.basePlan.description,
          quantity: 1,
          total: Number.isFinite(this.basePrice) ? this.basePrice : null,
        },
      ];

      this.moduleOptions.forEach(module => {
        if (this.localConfig.modules[module.id]) {
          items.push({
            id: module.id,
            name: module.name,
            description: module.description,
            quantity: 1,
            total: Number.isFinite(this.modulePrices[module.id])
              ? this.modulePrices[module.id]
              : null,
          });
        }
      });

      this.quantityOptions.forEach(item => {
        const qty = this.localConfig.quantities[item.id] || 0;
        if (qty > 0) {
          items.push({
            id: item.id,
            name: item.name,
            description: item.description,
            quantity: qty,
            total: Number.isFinite(this.quantityPrices[item.id])
              ? qty * this.quantityPrices[item.id]
              : null,
          });
        }
      });

      return items;
    },
  },
  watch: {
    config: {
      deep: true,
      handler(newValue) {
        this.localConfig = this.normalizeConfig(newValue);
      },
    },
  },
  methods: {
    normalizeInitialConfig(config = {}) {
      // Método para inicialização que não depende de computed properties
      const basePrice = resolveBasePrice(this.pricing);
      const modules = {};
      MODULE_IDS.forEach(moduleId => {
        modules[moduleId] = Boolean(config.modules?.[moduleId]);
      });

      const quantities = {};
      QUANTITY_IDS.forEach(itemId => {
        const parsed = Number.parseInt(config.quantities?.[itemId], 10);
        quantities[itemId] = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      });

      return {
        base_price: basePrice,
        modules,
        quantities,
      };
    },
    normalizeConfig(config = {}) {
      const basePrice = resolveBasePrice(this.pricing);
      const modules = {};
      this.moduleOptions.forEach(module => {
        modules[module.id] = Boolean(config.modules?.[module.id]);
      });

      const quantities = {};
      this.quantityOptions.forEach(item => {
        const parsed = Number.parseInt(config.quantities?.[item.id], 10);
        quantities[item.id] = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      });

      return {
        base_price: basePrice,
        modules,
        quantities,
      };
    },
    buildConfig() {
      return {
        base_price: this.basePrice,
        modules: { ...this.localConfig.modules },
        quantities: { ...this.localConfig.quantities },
        estimated_total: this.totalPrice,
      };
    },
    emitConfig() {
      if (this.readonly) return;
      this.$emit('update:config', this.buildConfig());
    },
    toggleModule(moduleId) {
      if (this.readonly) return;
      if (!this.isModulePriceAvailable(moduleId)) return;
      this.localConfig.modules[moduleId] = !this.localConfig.modules[moduleId];
      this.emitConfig();
    },
    adjustQuantity(itemId, delta) {
      if (this.readonly) return;
      if (!this.isQuantityPriceAvailable(itemId)) return;
      const current = this.localConfig.quantities[itemId] || 0;
      const nextValue = Math.max(0, current + delta);
      this.localConfig.quantities[itemId] = nextValue;
      this.emitConfig();
    },
    updateQuantity(itemId, value) {
      if (this.readonly) return;
      if (!this.isQuantityPriceAvailable(itemId)) return;
      const parsed = Number.parseInt(value, 10);
      const nextValue = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      this.localConfig.quantities[itemId] = nextValue;
      this.emitConfig();
    },
    formatPrice(price) {
      if (!Number.isFinite(price)) {
        return this.$t('PLANS.PRICE_UNAVAILABLE');
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(price);
    },
    isBasePriceAvailable() {
      return Number.isFinite(this.basePrice);
    },
    isModulePriceAvailable(moduleId) {
      return Number.isFinite(this.modulePrices[moduleId]);
    },
    isQuantityPriceAvailable(itemId) {
      return Number.isFinite(this.quantityPrices[itemId]);
    },
    isSelectionPriced() {
      if (!this.isBasePriceAvailable()) return false;

      const missingModulePrice = Object.keys(this.localConfig.modules).some(
        key => this.localConfig.modules[key] && !this.isModulePriceAvailable(key)
      );
      if (missingModulePrice) return false;

      const missingQuantityPrice = Object.keys(this.localConfig.quantities).some(
        key => Number(this.localConfig.quantities[key]) > 0 && !this.isQuantityPriceAvailable(key)
      );
      if (missingQuantityPrice) return false;

      return true;
    },
    continueSignup() {
      if (this.readonly) return;
      if (!this.isSelectionPriced()) return;
      this.$emit('continue', this.buildConfig());
    },
    goBack() {
      this.$emit('back');
    },
  },
};
</script>

<template>
  <div class="modular-plan max-w-[972px] mx-auto">
    <div v-if="summaryOnly" class="p-6 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-woot-50 mb-4">
        {{ $t('PLANS.MODULAR.SUMMARY.TITLE') }}
      </h3>
      <div class="space-y-3">
        <div
          v-for="item in selectedLineItems"
          :key="item.id"
          class="flex items-start justify-between gap-3 text-sm"
        >
          <div>
            <p class="text-slate-700 dark:text-slate-200 font-medium">
              {{ item.name }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ item.description }}
            </p>
            <p
              v-if="item.quantity > 1"
              class="text-xs text-slate-500 dark:text-slate-400"
            >
              {{ $t('PLANS.MODULAR.QUANTITIES.QUANTITY_LABEL') }}: {{ item.quantity }}
            </p>
          </div>
          <span class="text-slate-700 dark:text-slate-200 font-medium">
            {{ formatPrice(item.total) }}
          </span>
        </div>
      </div>
      <div class="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4 flex items-center justify-between">
        <span class="text-sm font-medium text-slate-600 dark:text-slate-300">
          {{ $t('PLANS.MODULAR.SUMMARY.TOTAL_ESTIMATED') }}
        </span>
        <span class="text-lg font-semibold text-responza-500 dark:text-responza-400">
          {{ formatPrice(totalPrice) }}
        </span>
      </div>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-3">
        {{ $t('PLANS.MODULAR.SUMMARY.DISCLAIMER') }}
      </p>
    </div>

    <template v-else>
      <button
        v-if="showBackButton"
        type="button"
        class="flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 mb-6 transition-colors"
        @click="goBack"
      >
        <svg
          class="w-5 h-5 mr-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {{ $t('PLANS.MODULAR.BACK') || $t('REGISTER.BACK') }}
      </button>

      <div v-if="showHeader">
        <h2 class="text-2xl font-semibold text-slate-900 dark:text-woot-50 mb-2">
          {{ $t('PLANS.MODULAR.TITLE') }}
        </h2>
        <p class="text-slate-600 dark:text-slate-400 mb-8">
          {{ $t('PLANS.MODULAR.SUBTITLE') }}
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div class="lg:col-span-2 space-y-6">
          <section class="p-6 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl">
            <div class="flex items-start justify-between gap-6">
              <div>
                <h3 class="text-lg font-semibold text-slate-900 dark:text-woot-50">
                  {{ basePlan.name }}
                </h3>
                <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {{ basePlan.description }}
                </p>
              </div>
              <div class="text-right">
                <p class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {{ $t('PLANS.MODULAR.BASE.VALUE') }}
                </p>
                <p class="text-xl font-semibold text-responza-500 dark:text-responza-400">
                  {{ formatPrice(basePlan.price) }}
                </p>
              </div>
            </div>
          </section>

          <section class="p-6 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-slate-900 dark:text-woot-50">
                {{ $t('PLANS.MODULAR.MODULES.TITLE') }}
              </h3>
              <span class="text-xs text-slate-500 dark:text-slate-400">
                {{ $t('PLANS.MODULAR.MODULES.TOGGLE_HINT') }}
              </span>
            </div>
            <div class="divide-y divide-slate-200 dark:divide-slate-700">
              <div
                v-for="module in modules"
                :key="module.id"
                class="py-4 first:pt-0 last:pb-0"
              >
                <div class="flex items-start justify-between gap-6">
                  <div>
                    <p class="text-sm font-medium text-slate-900 dark:text-woot-50">
                      {{ module.name }}
                    </p>
                    <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {{ module.description }}
                    </p>
                    <p class="text-sm text-responza-500 dark:text-responza-400 mt-2">
                      <span v-if="isModulePriceAvailable(module.id)">
                        + {{ formatPrice(module.price) }} {{ $t('PLANS.PER_MONTH') }}
                      </span>
                      <span v-else>
                        {{ $t('PLANS.PRICE_UNAVAILABLE') }}
                      </span>
                    </p>
                  </div>
                  <label class="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      class="sr-only"
                      :checked="localConfig.modules[module.id]"
                      :disabled="readonly || !isModulePriceAvailable(module.id)"
                      @change="toggleModule(module.id)"
                    />
                    <span
                      class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                      :class="
                        localConfig.modules[module.id]
                          ? 'bg-responza-500'
                          : 'bg-slate-200 dark:bg-slate-600'
                      "
                    >
                      <span
                        class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                        :class="localConfig.modules[module.id] ? 'translate-x-6' : 'translate-x-1'"
                      />
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          <section class="p-6 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl">
            <div class="mb-4">
              <h3 class="text-lg font-semibold text-slate-900 dark:text-woot-50">
                {{ $t('PLANS.MODULAR.QUANTITIES.TITLE') }}
              </h3>
              <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {{ $t('PLANS.MODULAR.QUANTITIES.SUBTITLE') }}
              </p>
            </div>
            <div class="divide-y divide-slate-200 dark:divide-slate-700">
              <div
                v-for="item in quantities"
                :key="item.id"
                class="py-4 first:pt-0 last:pb-0"
              >
                <div class="flex items-start justify-between gap-6">
                  <div>
                    <p class="text-sm font-medium text-slate-900 dark:text-woot-50">
                      {{ item.name }}
                    </p>
                    <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {{ item.description }}
                    </p>
                    <p class="text-sm text-responza-500 dark:text-responza-400 mt-2">
                      <span v-if="isQuantityPriceAvailable(item.id)">
                        + {{ formatPrice(item.price) }}/unidade
                      </span>
                      <span v-else>
                        {{ $t('PLANS.PRICE_UNAVAILABLE') }}
                      </span>
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:border-responza-500 hover:text-responza-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      :disabled="readonly || localConfig.quantities[item.id] === 0 || !isQuantityPriceAvailable(item.id)"
                      @click="adjustQuantity(item.id, -1)"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="0"
                      class="reset-base w-16 text-center px-2 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-responza-500 focus:border-responza-500"
                      :value="localConfig.quantities[item.id]"
                      :disabled="readonly || !isQuantityPriceAvailable(item.id)"
                      @input="updateQuantity(item.id, $event.target.value)"
                    />
                    <button
                      type="button"
                      class="w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:border-responza-500 hover:text-responza-500 transition-colors"
                      :disabled="readonly || !isQuantityPriceAvailable(item.id)"
                      @click="adjustQuantity(item.id, 1)"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <aside class="lg:col-span-1 lg:sticky self-start" :class="summaryTopClass">
          <div class="p-6 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
            <h3 class="text-lg font-semibold text-slate-900 dark:text-woot-50 mb-4">
              {{ $t('PLANS.MODULAR.SUMMARY.TITLE') }}
            </h3>
            <div class="space-y-3">
              <div
                v-for="item in selectedLineItems"
                :key="item.id"
                class="flex items-start justify-between gap-3 text-sm"
              >
                <div>
                  <p class="text-slate-700 dark:text-slate-200 font-medium">
                    {{ item.name }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ item.description }}
                  </p>
                  <p
                    v-if="item.quantity > 1"
                    class="text-xs text-slate-500 dark:text-slate-400"
                  >
                    {{ $t('PLANS.MODULAR.QUANTITIES.QUANTITY_LABEL') }}: {{ item.quantity }}
                  </p>
                </div>
                <span class="text-slate-700 dark:text-slate-200 font-medium">
                  {{ formatPrice(item.total) }}
                </span>
              </div>
            </div>
            <div class="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4 flex items-center justify-between">
              <span class="text-sm font-medium text-slate-600 dark:text-slate-300">
                {{ $t('PLANS.MODULAR.SUMMARY.TOTAL_ESTIMATED') }}
              </span>
              <span class="text-lg font-semibold text-responza-500 dark:text-responza-400">
                {{ formatPrice(totalPrice) }}
              </span>
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-3">
              {{ $t('PLANS.MODULAR.SUMMARY.DISCLAIMER') }}
            </p>
          </div>
        </aside>
      </div>

      <div
        v-if="showFooter"
        class="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ $t('PLANS.MODULAR.SUMMARY.ADJUSTABLE') }}
        </p>
        <button
          type="button"
          class="w-full sm:w-auto py-3 px-6 bg-responza-500 hover:bg-responza-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!isSelectionPriced()"
          @click="continueSignup"
        >
          {{ $t('PLANS.MODULAR.CONTINUE') }}
        </button>
      </div>
    </template>
  </div>
</template>
